/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-runloop, warp-drive/no-legacy-request-patterns */
// Extend From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import QrStatus from '@linc-technologies/linced-common/enums/qr-status';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { tagName } from '@ember-decorators/component';

/**
 * errors provides a container of all errors thrown in this module.
 *
 * @type {{GenericError: string, PeopleNotFound: string}}
 */
const errors = {
  GenericError: 'Generating QR codes failed, please try again',
  PeopleNotFound:
    'Unable to generate QR Codes for a group without current learners',
  RegenError: 'Regenerating QR Code failed, please try again',
};

@tagName('')
export default class QrCodeManager extends Component {
  QrStatus = QrStatus;

  // TODO: remove `heroFeatureEnabled` flag once feature is GA
  @optionAlias('hero:features:qrcodes')
  heroFeatureEnabled;

  @optionAlias('core:staffPermissions:generateQrCodes')
  schoolFeatureEnabled;

  @computed('heroFeatureEnabled', 'schoolFeatureEnabled')
  get qrCodesEnabled() {
    return this.heroFeatureEnabled && this.schoolFeatureEnabled;
  }

  // Services
  @service('hero-api')
  api;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Params
  /**
   * iconButton configures whether the button should render as an icon button,
   * or a full-sized button.
   *
   * @param {Boolean}
   */
  iconButton = false;

  /**
   * group provides a single group to build a list of current learner ids to
   * generate sign in QR Codes for.
   * Will be overridden by personIds if supplied.
   *
   * @param {DS.Model<module:linced-common/models/group>}
   */
  group = null;

  /**
   * personId provides a single unique person identifier.
   * Will be overridden by personIds if supplied.
   *
   * @param {String}
   */
  personId = null;

  /**
   * personIds provides an array of unique person identifiers to generate QR
   * Codes for.
   *
   * @param {String[]}
   */
  personIds = null;

  /**
   * size configures the render size of the paper icon.
   *
   * @param {number}
   */
  size = 24;

  /**
   * raised configures the QR Sign In button to be raised.
   *
   * @param {Boolean}
   */
  raised = true;

  // State
  /**
   * dataIsLoading tracks person and qr code data being loaded into the store.
   */
  @tracked
  dataIsLoading = false;

  /**
   * dialogIsOpen tracks whether the QR Display panel is open.
   */
  @tracked
  dialogIsOpen = false;

  /**
   * qrCodes tracks the underlying QR Code data to be displayed to the end user.
   */
  @tracked
  qrCodes = null;

  /**
   * recordIsLoading tracks whether an individual qr code is being loaded into
   * the store.
   */
  recordIsLoading = false;

  // Methods
  /**
   * getPersonIds reduces the different parameter inputs down to a single
   * returned string array of person ids.
   *
   * @return {String[]} - An array of unique person ids.
   */
  getPersonIds() {
    let personIds = [];
    if (this.group) {
      // If a group is provided, set personIds to the list of current learners
      // within the group.
      personIds = this.group.currentLearnerMemberIds;
    } else if (this.personId) {
      // If a person id is provided, set the individual.
      personIds.push(this.personId);
    } else if (this.personIds) {
      // if a raw array of person ids is provided to the component, set them.
      personIds = this.personIds;
    }

    return personIds;
  }

  /**
   * preloadDatastore given an array of person ids, queries the API for the
   * required records.
   *
   * @param {String[]} personIds - An array of unique person ids.
   * @return {(Promise<DS.AdapterPopulatedRecordArray<module:app/models/person>|DS.Store.Model<module:app/models/person>|null>)}
   */
  preloadDatastore(personIds) {
    if (this.group) {
      // If we have a group, load all the people into the store, so that the qr
      // codes can be properly sorted (fname, lname) when the store hasn't been
      // pre-populated with students.
      return this.store.query('person', {
        basic: true,
        groupId: this.group.id,
        type: PersonType.LEARNER,
      });
    }

    if (personIds.length > 1) {
      // If no group is provided, but we have multiple ids provided, attempt to
      // retrieve the people directly.
      return this.store.query('person', {
        basic: true,
        type: PersonType.LEARNER,
        people: JSON.stringify(personIds),
      });
    }

    // Else, for a single person, attempt to retrieve the person locally, and
    // if not found, perform a network request.
    let personId = personIds[0];
    let record = this.store.peekRecord('person', personId);
    if (record) {
      return Promise.resolve(record);
    }

    return this.store.findRecord('person', personId);
  }

  /**
   * setQrCodes checks if we have received a valid QR Payload, sorts if required
   * or throws an error if a payload is not received.
   *
   * @param {(DS.AdapterPopulatedRecordArray<module:linced-common/models/auth/qr-code>|null)} qrCodes - an array of QR Code models.
   * @throws {errors.GenericError}
   */
  setQrCodes(qrCodes) {
    if (isEmpty(qrCodes)) {
      this.reset();
      throw errors.GenericError;
    }

    // sort the array for human consumption.
    qrCodes = sortBy(qrCodes, 'person.firstName', 'person.lastName');

    this.qrCodes = qrCodes;
  }

  /**
   * reset returns the component back to an expected starting point.
   */
  reset() {
    this.dataIsLoading = false;
    this.dialogIsOpen = false;
    this.qrCodes = null;

    // Destroy all locally stored QR Codes as they are single use anyway.
    this.store.unloadAll('auth/qr-code');
  }

  /**
   * openDialog opens the qr code display dialog, which sits in a loading
   * phase until all required person records are present and the requested QR
   * Codes have been generated by the backend, ready to be displayed.
   */
  @action
  openDialog() {
    // Open dialog and set to loading state.
    this.toggleProperty('dataIsLoading');
    this.toggleProperty('dialogIsOpen');

    // Reduce component parameters down to a single array of person ids.
    let personIds = this.getPersonIds();
    if (!personIds || personIds.length === 0) {
      // return early if we've got nothing to process.
      this.reset();
      this.toaster.error(errors.PeopleNotFound);
      return;
    }

    // Preload people records and generate QR Codes.
    hash({
      _: this.preloadDatastore(personIds),
      qrCodes: this.api.auth.getQrCodes(this.session.activeSchool, personIds),
    })
      .then((values) => this.setQrCodes(values.qrCodes))
      .then(() => {
        this.toggleProperty('dataIsLoading');
      })
      .catch((err) => {
        this.reset();
        this.toaster.error(err);
      });
  }

  /**
   * regenerateQrCode given a person id and index, requests a new single use
   * sign in QR Code.
   *
   * @param {number} index - The position of the QR Code array to inject the new record.
   * @param {String} personId - The unique person to regenerate a QR Code for.
   */
  @action
  regenerateQrCode(index, personId) {
    if (index < 0 || index > this.qrCodes.length - 1) {
      // invalid index specified...
      return;
    }

    this.toggleProperty('recordIsLoading');
    this.api.auth
      .getQrCodes(this.session.activeSchool, [personId])
      .then((qrCode) => {
        // mutate the stored array of qr codes...
        let qrCodes = this.qrCodes;
        qrCodes[index] = qrCode[0];
        this.qrCodes = qrCodes;
      })
      .catch(() => {
        this.toaster.error(errors.RegenError);
      })
      .finally(() => {
        this.toggleProperty('recordIsLoading');
      });
  }

  /**
   * closeDialog closes the dialog and unloads all QR Code records.
   */
  @action
  closeDialog() {
    this.reset();
  }

  /**
   * printQrCodes generates print specific HTML for the received QR Codes and
   * pushes it out into a new window ready for printing.
   */
  @action
  printQrCodes() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );

    printer.document.body.innerHTML = qrCodesPrintHTML(this.qrCodes);

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }
}

/**
 * qrCodesPrintHTML builds and returns the expected HTML in order to print out a
 * given list of QR Codes.
 *
 * @param {DS.AdapterPopulatedRecordArray<module:linced-common/models/auth/qr-code>} qrCodes - an array of QR Code records from ember data.
 * @returns {String} - A rendered HTML string containing QR Codes with styling for a print layout.
 */
function qrCodesPrintHTML(qrCodes) {
  let printHtml = qrCodesPrintCSS();

  for (let i = 0; i < qrCodes.length; i++) {
    let qrCode = qrCodes[i];
    if (!qrCode || qrCode.status !== QrStatus.OK) {
      // Don't print an invalid QR Code.
      continue;
    }

    printHtml += qrCodePrintHTML(qrCode);
  }

  return printHtml;
}

/**
 * qrCodesPrintCSS returns CSS style overrides for printing out QR Codes, while
 * pulling in our pre-defined styles.
 *
 * @returns {String} - A string containing HTML CSS directives.
 */
function qrCodesPrintCSS() {
  return `
<link rel="stylesheet" href="${window.location.origin}/assets/icons/linced-material.css">
<link rel="stylesheet" href="${window.location.origin}/assets/linced-app.css">
<link rel="stylesheet" href="${window.location.origin}/assets/vendor.css">
<style>
  h1 {
    text-transform: uppercase;
  }

  h2 {
    text-transform: uppercase;
  }
</style>
`;
}

/**
 * qrCodePrintHTML returns a rendered block of HTML for a single QR Code.
 *
 * @param {DS.Model<module:linced-common/models/auth/qr-code>} qrCode - A single QR Code ember data record.
 * @returns {String} - A string of parsed HTML for a single QR Code.
 */
function qrCodePrintHTML(qrCode) {
  if (!qrCode) {
    return '';
  }

  return `
<div>
  <div class="layout-column layout-align-center-center">
    <h1>${qrCode.get('person.fullName')}</h1>
    <h2>Expires: ${qrCode.get('expiry')}</h2>
  </div>

  <div class="layout-row layout-align-center-center">
    <img
      src="${qrCode.get('src')}"
      height="512px"
      width="512px"
    >
  </div>
</div>
<p style="page-break-after: always;">&nbsp;</p>
`;
}
